import { API_ENRICHED_PROFILE, GET_USER_PREFERENCES } from "./constants/URLS";
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { config } from "./config";
import { trackMixpanelEvent } from "./utils/helpers";
import { QueryCache, QueryClient } from "@tanstack/react-query";
import { useRefreshTokenStore } from "./useRefreshTokenStore";
import { refreshToken } from "./appInterface";

export const mockHttpClient = axios.create({
    baseURL: "http://demo5213211.mockable.io/",
    // baseURL: config.baseUrl,
});

export const httpClient =  axios.create({
    baseURL: config.baseUrl,
});

export const infraHttpClient = axios.create({
    baseURL: config.infra_base_url,
});

export const httpLegacyClient = axios.create({
    baseURL: config.legacyBaseUrl,
});

export const httpCommunityClient = axios.create({
    baseURL: config.communityBaseUrl,
});

let IS_SUBSCRIBED_TO_TOKEN_STORE = false;
const ERROR_CODES_REQUIRE_TOKEN_REFRESH = [401];
const QUERY_STATUS_REQUIRE_REFETCH = ["loading", "error"];

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 20000,
            onError: async (error) => {
                /**
                 * We can't have this as a constant as query cache takes time to be populated,
                 * and we would require updated queries after the new token has been fetched.
                 * @returns An array of required queries to be refetched
                 */
                const getQueriesToRetry = () =>
                    queryClient
                        .getQueryCache()
                        .getAll()
                        .filter((query) =>
                            QUERY_STATUS_REQUIRE_REFETCH.includes(query.state.status)
                        );
                /**
                 * We are using a zustand store and subscribing to the state,
                 * for trigerring the callbacks, instead of running an interval based check.
                 */
                const {
                    isTokenRefreshed,
                    isTokenRefreshing,
                    setTokenRefreshing,
                    setTokenRefreshed,
                } = useRefreshTokenStore.getState();
                if (
                    ERROR_CODES_REQUIRE_TOKEN_REFRESH.includes(
                        (error as AxiosError).response?.status as number
                    )
                ) {
                    /**
                     * if it's the first time, set the subscription flag to true, so that we
                     * don't have multiple subscriptions open to the state, which will hamper the performance.
                     */
                    if (!IS_SUBSCRIBED_TO_TOKEN_STORE) {
                        IS_SUBSCRIBED_TO_TOKEN_STORE = true;
                        const unsubscribeToStore = useRefreshTokenStore.subscribe(
                            (state) => state.isTokenRefreshed,
                            async (updatedIsTokenRefreshed) => {
                                /**
                                 * Once token has been refreshed we loop through all the queries,
                                 * to refetch them.
                                 */
                                if (updatedIsTokenRefreshed) {
                                    const queriesToRetry = getQueriesToRetry();
                                    for (const query of queriesToRetry) {
                                        /**
                                         * We need to use fetchQuery, as the other refetch mechanisms like
                                         * refetchQueries and invalidateQueries will work only when this query has
                                         * been cached, which is not in case a query has errored.
                                         */
                                        queryClient.fetchQuery(query.queryKey);
                                    }
                                    unsubscribeToStore();
                                }
                            }
                        );
                    }

                    if (isTokenRefreshing && !isTokenRefreshed) {
                        /**
                         * Once the call fails for 401, the refresh is in progress, any other 401 failures
                         * shouldn't have multiple retries. This will make sure that the queries are not
                         * getting retried unless token has been refreshed.
                         */
                        const queriesToRetry = getQueriesToRetry();
                        for (const query of queriesToRetry) {
                            queryClient.cancelQueries({ queryKey: query.queryKey });
                        }
                    }

                    if (!isTokenRefreshing && !isTokenRefreshed) {
                        // Runs if the token has expired and not yet started refreshing.
                        const currentToken = sessionStorage.getItem("__token__");
                        // Set the gloabl refreshing variable to true before refresh token is called.
                        setTokenRefreshing(true);
                        refreshToken(currentToken as string);
                        /**
                         * -----------------------------------------------------------------------------
                         * This block of code can be uncommented to test the flow. As refresh won't work
                         * unless we have the android bridge
                         * -----------------------------------------------------------------------------
                         */
                        // setTimeout(() => {
                        //     setTokenRefreshed(true);
                        //     setTokenRefreshing(false)
                        // }, 20000);
                    }
                }
            },
        },
    },
});

const authInterceptor = function (config: AxiosRequestConfig<any>) {
    const authToken = sessionStorage.getItem("__token__") || import.meta.env.VITE_TOKEN;
    if (!authToken) return config;

    return {
        ...config,
        headers: {
            ...config.headers,
            Authorization: `Token ${authToken}`,
        },
    };
};

const checkIfGetEnrichedCall = (error: AxiosError) => {
    return (
        error?.config?.method === "get" &&
        error?.config?.url?.includes(API_ENRICHED_PROFILE)
    );
};

const isPrefsCall = (error: AxiosError) => {
    return (
        error?.config?.method?.toLowerCase() === "get" &&
        error?.response?.status === 404 &&
        error?.config?.url?.includes(GET_USER_PREFERENCES)
    );
};

const triggerAPIErrorToast = (message: string) =>
    toast.error(message, {
        position: "top-right",
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
        theme: "colored",
    });

const responseErrorHandler = (error: AxiosError) => {
    const errorPayload = {
        errorCode: error.code,
        message: error.message,
        name: error.name,
        url: error.config?.url,
        baseUrl: error.config?.baseURL,
        method: error.config?.method,
        data: error.config?.data,
        headers: error.config?.headers,
        responseData: error.response?.data,
        responseStatusCode: error.response?.status,
        responseStatusText: error.response?.statusText,
    };
    trackMixpanelEvent("API error", errorPayload);

    const isNetworkError =
        error.message === "Network Error" ||
        !error.response ||
        !error.response.status ||
        error.code === AxiosError.ERR_NETWORK;

    if (isNetworkError) {
        triggerAPIErrorToast(
            "Your internet connection is unstable, please check your internet status."
        );
        return Promise.reject(error);
    }

    if (
        error.response?.status !== 200 &&
        error.response?.status !== 401 &&
        !checkIfGetEnrichedCall(error) &&
        !isPrefsCall(error) &&
        error?.config?.url?.includes(API_ENRICHED_PROFILE)
    ) {
        triggerAPIErrorToast("Something went wrong with server response");
    }
    /**
     * Initially as the axios has it's own error interceptor, this restricts react query to trigger
     * it's own  interceptor. Here we are manually triggering the react query handler from axios
     * interceptor.
     */
    (queryClient as any).getDefaultOptions()?.queries?.onError(error);
    if (error.response?.status === 401) {
        if (typeof window !== "undefined") {
            // const queryParams = new URLSearchParams({
            //     return_to: window.location.href,
            // });
            // window.location.href = `/login?${queryParams.toString()}`;
        } else {
        }
    } else if (error.response?.status === 403) {
        if (typeof window !== "undefined") {
            window.location.href = "/";
        } else {
            console.log("Forbidden access", error);
        }
    } else {
        return Promise.reject(error);
    }
};

const responseHandler = (response: AxiosResponse) => response;

httpClient.interceptors.request.use(authInterceptor);
httpClient.interceptors.response.use(responseHandler, responseErrorHandler);

infraHttpClient.interceptors.request.use(authInterceptor);
infraHttpClient.interceptors.response.use(responseHandler, responseErrorHandler);

httpLegacyClient.interceptors.request.use(authInterceptor);
httpLegacyClient.interceptors.response.use(responseHandler, responseErrorHandler);

httpCommunityClient.interceptors.request.use(authInterceptor);
httpCommunityClient.interceptors.response.use(responseHandler, responseErrorHandler);

mockHttpClient.interceptors.request.use(authInterceptor);
mockHttpClient.interceptors.response.use(responseHandler, responseErrorHandler);
